import { Category } from "protos/robot_syncer/plant_profile";
import { CategoryChipPreviewBar } from "./CategoryChipPreviewBar";
import { CreateCategoryDialogActions } from "./CreateCategoryDialogActions";
import { DEBOUNCE_MS } from "portal/utils/timing";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { portalEndpoints } from "portal/state/portalApi";
import { SearchField } from "portal/components/header/SearchField";
import { useDebounce } from "@uidotdev/usehooks";
import { useInfiniteScrollOnScroll } from "portal/utils/hooks/useInfiniteScroll";
import { useTranslation } from "react-i18next";
import CategoryTemplatesIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import React, { FC, useCallback, useMemo, useState } from "react";

const PAGE_SIZE = 25;

type CopyFromTemplatePickStepProps = {
  customerUuid?: string;
  selectedCategory?: Category;
  ineligibleChipIds: ReadonlySet<string>;
  onSelectCategory: (category: Category) => void;
  onCancel: () => void;
  onBack: () => void;
  onNext: () => void;
};

export const CopyFromTemplatePickStep: FC<CopyFromTemplatePickStepProps> = ({
  customerUuid,
  selectedCategory,
  ineligibleChipIds,
  onSelectCategory,
  onCancel,
  onBack,
  onNext,
}) => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState("");
  const searchText = useDebounce(searchInput.trim(), DEBOUNCE_MS);

  const { data, hasNextPage, fetchNextPage, isFetching, isSuccess } =
    portalEndpoints.listCategoriesCursor.useInfiniteQuery({
      limit: PAGE_SIZE,
      search: searchText.trim() || undefined,
      inLibrary: true,
      customerUuid,
    });

  const categories = useMemo(
    () =>
      (data?.pages ?? [])
        .flatMap((page) => page.categories)
        .map((saved) => saved.category)
        .filter((category): category is Category => Boolean(category?.id)),
    [data]
  );

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetching]);

  const handleListScroll = useInfiniteScrollOnScroll(loadMore);

  const isEmptyTemplates =
    isSuccess && categories.length === 0 && !searchText.trim();

  const noResults =
    isSuccess && !isFetching && categories.length === 0 && searchText.trim();

  return (
    <>
      {isEmptyTemplates ? (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <CategoryTemplatesIcon
            color="disabled"
            className="text-5xl"
            aria-hidden
          />
          <Typography variant="h6" color="textSecondary">
            {t(
              "components.categoryCollectionProfile.actions.emptyTemplatesTitle"
            )}
          </Typography>
          <Typography color="textSecondary">
            {t(
              "components.categoryCollectionProfile.actions.emptyTemplatesDescription"
            )}
          </Typography>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <CategoryChipPreviewBar
            chipIds={selectedCategory?.chipIds ?? []}
            categoryId={selectedCategory?.id}
            ineligibleChipIds={ineligibleChipIds}
          />
          <SearchField
            value={searchInput}
            onChange={setSearchInput}
            fullWidth
          />
          <List
            disablePadding
            className="max-h-64 overflow-y-auto"
            onScroll={handleListScroll}
          >
            {categories.map((category) => {
              const selected = category.id === selectedCategory?.id;
              return (
                <ListItemButton
                  disabled={isFetching}
                  key={category.id}
                  divider
                  className={selected ? "!bg-carbon-orange/40" : undefined}
                  selected={selected}
                  onClick={() => onSelectCategory(category)}
                >
                  <ListItemIcon>
                    <CategoryTemplatesIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${category.name} (${category.chipIds.length})`}
                  />
                </ListItemButton>
              );
            })}
            {noResults && (
              <Typography color="textSecondary" className="px-4 py-2">
                {t("utils.lists.noResults")}
              </Typography>
            )}
          </List>
        </div>
      )}
      <CreateCategoryDialogActions
        onBack={onBack}
        onCancel={onCancel}
        onConfirm={onNext}
        confirmLabel={t("utils.actions.next")}
        confirmDisabled={!selectedCategory}
      />
    </>
  );
};
